
import { mergeMap, map, catchError } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import { EpicRefined } from 'interfaces/middleware'
import {
  CREATE_STRIPE_PAYMENT_METHOD,
  PATCH_STRIPE_PAYMENT_METHOD,
  createStripePaymentMethodActionCreator,
  patchStripePaymentMethodActionCreator,
  StripeAction,
} from 'actions/stripeActions'
import { POST_STRIPE_PAYMENT_METHOD_URL } from 'interfaces/ajax'
import { merge, from, of } from 'rxjs'
import { getStripe, getElements } from 'helpers/stripeHelpers'
import { captureWarning, isAjaxError } from 'helpers/errorHelpers'
import { GENERIC_PAYMENT_ERROR, USE_ANOTHER_PAYMENT_METHOD_ERROR } from 'constants/stripe'
import { CardNumberElement } from '@stripe/react-stripe-js'
import { Subscription } from 'interfaces/subscription'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { ajaxCatch, AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'

const createPaymentMethodEpic: EpicRefined<StripeAction | AjaxCatchExtraActions> = (action$, _store, { post, patch }) => {
  const createAction$ = action$
    .pipe(filterRequestOfType(CREATE_STRIPE_PAYMENT_METHOD, 'request'))

  const patchAction$ = action$
    .pipe(filterRequestOfType(PATCH_STRIPE_PAYMENT_METHOD, 'request'))

  return merge(createAction$, patchAction$).pipe(
    mergeMap((action) => {
      const successAction = action.type === CREATE_STRIPE_PAYMENT_METHOD
        ? (response: Subscription) => createStripePaymentMethodActionCreator.success(action.payload.params, response)
        : (response: Subscription) => patchStripePaymentMethodActionCreator.success(action.payload.params, response)

      const failAction = action.type === CREATE_STRIPE_PAYMENT_METHOD
        ? (errorCode = 0, errorExtra?: string[]) => createStripePaymentMethodActionCreator.fail(action.payload.params, errorCode, errorExtra)
        : (errorCode = 0, errorExtra?: string[]) => patchStripePaymentMethodActionCreator.fail(action.payload.params, errorCode, errorExtra)

      return from(getStripe()).pipe(
        map((stripe) => {
          if (!stripe) {
            captureWarning('Stripe: unexpected `stripe` object', { stripe })
            throw new Error(GENERIC_PAYMENT_ERROR)
          }

          const elements = getElements()
          if (!elements) {
            captureWarning('Stripe: unexpected `elements` object', { elements })
            throw new Error(GENERIC_PAYMENT_ERROR)
          }

          const card = elements.getElement(CardNumberElement)
          if (!card) {
            captureWarning('Stripe: unexpected `card` object', { card })
            throw new Error(GENERIC_PAYMENT_ERROR)
          }

          return { stripe, card }
        }),
        mergeMap(({ stripe, card }) => stripe.createPaymentMethod({
          type: 'card',
          card,
        })),
        mergeMap(({ paymentMethod, error }) => {
          if (error) {
            captureWarning('Stripe: createPaymentMethod error', { error, paymentMethod })
            throw new Error(USE_ANOTHER_PAYMENT_METHOD_ERROR)
          }

          if (!paymentMethod) {
            captureWarning('Stripe: unexpected createPaymentMethod result', { paymentMethod })
            throw new Error(GENERIC_PAYMENT_ERROR)
          }

          const { amount } = action.payload.params
          const body = {
            payment_method_id: paymentMethod.id,
            amount: `${ amount }`,
          }

          if (action.type === CREATE_STRIPE_PAYMENT_METHOD) {
            return post(POST_STRIPE_PAYMENT_METHOD_URL, { body })
          }
          if (action.type === PATCH_STRIPE_PAYMENT_METHOD) {
            return patch(POST_STRIPE_PAYMENT_METHOD_URL, { body })
          }

          captureWarning('Stripe: unexpected action', { action })
          throw new Error(GENERIC_PAYMENT_ERROR)
        }),
        mergeMap(({ response }) => {
          if (response.type) {
            return of(successAction(response))
          }

          throw new Error(USE_ANOTHER_PAYMENT_METHOD_ERROR)
        }),
        catchError((error: Error) => {
          if (isAjaxError(error)) {
            throw error
          }

          captureWarning('Stripe: exception', { error })
          return of(failAction(0, [error.message]))
        })
      )
        .pipe(
          ajaxCatch((error) => {
            const { response, statusText, status } = error.xhr
            if (status === 542) {
              return of(failAction(error.status, [USE_ANOTHER_PAYMENT_METHOD_ERROR]))
            }

            if (!response || typeof response !== 'object') {
              const message = statusText && statusText.toLowerCase() !== 'unknown'
                ? statusText
                : USE_ANOTHER_PAYMENT_METHOD_ERROR

              return of(failAction(error.status, [message]))
            }

            let errors: string[] = []
            try {
              const keys = Object.keys(response)
              errors = response[keys[0]]
            } catch (e) {}

            return of(failAction(error.status, errors))
          })
        )

    }),
  )
}

// const successStripePatchEpic: Epic = (action$, store) => action$.pipe(
//   filterRequestOfType(PATCH_STRIPE_PAYMENT_METHOD, 'success'),
//   mergeMap(() => {
//     const state = store.getState()
//     const isSubscribed = isMemberPremiumSelector(state)
//     if (isSubscribed) {
//       return of(killFloatingPanesActionCreator('subscription'))
//     }

//     return EMPTY
//   })
// )

export const stripeEpic = combineEpics(
  createPaymentMethodEpic,
  // successStripePatchEpic,
)
